import React, { useState, useEffect } from "react";
import { IconPlus, IconTrash, IconSearch } from "@tabler/icons-react";
import { LangConstant, SystemConstant } from "const";
import { useTranslation } from "react-i18next";
import { ConfirmDialog, Pagination } from "components";
import { useDeleteMember, useRole } from "hooks";

const MemberDialog = ({
  open,
  selectedDepartment,
  currentPageUsers,
  totalPages,
  page,
  onFilter,
  onOpenAddMemberDialog,
  onClose,
}) => {
  const { t: getLabel } = useTranslation();
  const { isServiceAdmin } = useRole();

  const userDetail = getLabel(LangConstant.OBJ_USER_DETAIL, { returnObjects: true });

  const { deleteMemberAsync } = useDeleteMember();

  const [deleteMemberId, setDeleteMemberId] = useState(null);

  const handleChangeSearch = event => {
    onFilter({ searchValue: event.target.value, page: SystemConstant.ACTIVE_PAGINATION });
  };

  const handleChangePage = (page) => {
    onFilter({ page: page });
  };

  const handleOpenDeleteMember = userId => {
    setDeleteMemberId(userId);
  };

  const handleCloseDeleteMember = () => {
    setDeleteMemberId(null);
  };

  const handleDeleteMember = () => {
    const userIds = [deleteMemberId];
    deleteMemberAsync({
      departmentId: selectedDepartment.departmentId,
      userIds: userIds,
      unitId: selectedDepartment.unitId,
      onClose: handleCloseDeleteMember,
    });
  };

  // ESC key handler
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = e => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="modal-backdrop fade show" style={{ zIndex: 1040 }}></div>
      <div className="modal show d-block" tabIndex="-1" style={{ zIndex: 1050 }}>
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content" style={{ maxHeight: "80vh" }}>
            <div className="modal-header">
              <h5 className="modal-title" style={{ fontWeight: 600 }}>
                {getLabel(LangConstant.TXT_MEMBER_LIST)}
              </h5>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </div>
            <div className="modal-body" style={{ overflowY: "auto" }}>
              <div className="d-flex justify-content-between align-items-center">
                <div className="input-group" style={{ margin: "10px 0" }}>
                  <span className="input-group-text">
                    <IconSearch size={16} />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    onChange={handleChangeSearch}
                    placeholder={getLabel(
                      isServiceAdmin
                        ? LangConstant.P_SEARCH_USER_BY_NAME_PHONE
                        : LangConstant.P_SEARCH_USER_BY_NAME_PHONE_EMAIL,
                    )}
                  />
                </div>
                <button
                  className="btn btn-primary d-flex align-items-center gap-1"
                  style={{ minWidth: "fit-content", marginLeft: 24 }}
                  onClick={onOpenAddMemberDialog}
                  disabled={!selectedDepartment?.departmentId}
                >
                  <IconPlus size={16} />
                  {getLabel(LangConstant.TXT_ADD_MEMBER)}
                </button>
              </div>
              <div style={{ marginTop: 16 }}>
                <table className="table table-vcenter" style={{ whiteSpace: "nowrap", tableLayout: "fixed" }}>
                  <thead>
                    <tr>
                      <th style={{ width: "35%", fontWeight: "bold" }}>{userDetail.userName}</th>
                      <th style={{ width: "25%", fontWeight: "bold" }}>{userDetail.phone}</th>
                      {!isServiceAdmin && (
                        <th style={{ width: "30%", fontWeight: "bold" }}>{userDetail.email}</th>
                      )}
                      <th style={{ width: "10%" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentPageUsers && currentPageUsers.length > 0 ? (
                      currentPageUsers.map((row, index) => (
                        <tr key={index}>
                          <td
                            title={row.name}
                            style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                          >
                            {row.name}
                          </td>
                          <td>{row.phone}</td>
                          {!isServiceAdmin && (
                            <td
                              title={row.mail}
                              style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
                            >
                              {row.mail}
                            </td>
                          )}
                          <td>
                            <button
                              className="btn btn-icon btn-ghost-danger"
                              onClick={() => handleOpenDeleteMember(row.id)}
                              disabled={!selectedDepartment?.departmentId}
                            >
                              <IconTrash size={18} />
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={isServiceAdmin ? 3 : 4} style={{ textAlign: "center" }}>
                          <small>
                            <i>{getLabel(LangConstant.TXT_NOT_FOUND)}</i>
                          </small>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <Pagination currentPage={page} totalPages={totalPages} onPageChange={(e, p) => handleChangePage(p)} />
              )}
              <ConfirmDialog
                open={Boolean(deleteMemberId)}
                title={getLabel(LangConstant.TXT_CONFIRM_DELETE_MEMBER)}
                onDeny={handleCloseDeleteMember}
                onConfirm={handleDeleteMember}
              />
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-outline-danger"
                style={{ width: 150 }}
                onClick={onClose}
              >
                {getLabel(LangConstant.L_CLOSE)}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MemberDialog;
